import { useReduxState } from 'hooks';
import { Button, FlexContainer } from 'components/common';
import {
  ArrayField,
  Code,
  FormGroup,
  FormSection,
  NumberStepper,
  Select
} from 'components/common/form';
import { SiteSettingsForm } from '.';

const SiteSettingsPdfs = () => {
  const {
    messages,
    serverConfig: { customPdfContent },
    theme
  } = useReduxState();

  const updateValuesBeforeSubmit = (values) => {
    if (!values.customPdfContent) values.customPdfContent = [];
    return values;
  };

  const initialValues = { customPdfContent };

  const scopeOptions = [
    { label: 'Global', value: 'global' },
    { label: 'Course', value: 'course' }
  ];

  return (
    <SiteSettingsForm
      initialValues={initialValues}
      padding={0}
      title="PDF exports"
      updateValuesBeforeSubmit={updateValuesBeforeSubmit}
    >
      {({ values }) => (
        <FormSection title="Custom content" maxWidth="100%">
          <ArrayField field="customPdfContent">
            {({ addWithInitialValue }) => {
              const usedScopes = (values?.customPdfContent || []).reduce(
                (acc, item) => {
                  acc[item.scope] = true;
                  return acc;
                },
                {} as Record<string, boolean>
              );

              let nextScope: string | undefined;
              for (let i = 0; i < scopeOptions.length; i++) {
                if (!usedScopes[scopeOptions[i].value]) {
                  nextScope = scopeOptions[i].value;
                  break;
                }
              }

              return (
                <>
                  <ArrayField.Items>
                    {({ remove, field }) => {
                      const openScopes = scopeOptions.filter(
                        (scope) => !usedScopes[scope.value]
                      );

                      return (
                        <FlexContainer
                          style={{
                            alignItems: 'flex-start',
                            border: `1px solid ${theme.lightGrey}`,
                            borderRadius: theme.cardBorderRadius,
                            marginBottom: 10,
                            padding: 10,
                            gap: 20,
                            width: '100%'
                          }}
                        >
                          <FlexContainer
                            style={{
                              alignItems: 'flex-start',
                              flexDirection: 'column',
                              flexGrow: 1
                            }}
                          >
                            <FormGroup
                              label="Header content"
                              maxWidth="100%"
                              style={{ width: '100%' }}
                            >
                              <Code
                                field={`${field}.headerContent`}
                                placeholder="<div style='font-size: 20rem; margin: 0 40px;'>Header content</div>"
                              />
                            </FormGroup>

                            <FormGroup
                              label="Footer content"
                              maxWidth="100%"
                              style={{ width: '100%' }}
                            >
                              <Code
                                field={`${field}.footerContent`}
                                placeholder="<div style='font-size: 20rem; margin: 0 40px;'>Footer content</div>"
                                style={{ flexGrow: 1 }}
                              />
                            </FormGroup>

                            <FormGroup
                              label="Margins"
                              style={{ width: '100%' }}
                            >
                              <div
                                style={{
                                  display: 'grid',
                                  gap: 10,
                                  gridTemplateColumns: 'repeat(2, 1fr)'
                                }}
                              >
                                <NumberStepper
                                  field={`${field}.margins.top`}
                                  label="Top"
                                  min={0}
                                  stepValue={1}
                                />

                                <NumberStepper
                                  field={`${field}.margins.right`}
                                  label="Right"
                                  min={0}
                                  stepValue={1}
                                />

                                <NumberStepper
                                  field={`${field}.margins.bottom`}
                                  label="Bottom"
                                  min={0}
                                  stepValue={1}
                                />

                                <NumberStepper
                                  field={`${field}.margins.left`}
                                  label="Left"
                                  min={0}
                                  stepValue={1}
                                />
                              </div>
                            </FormGroup>

                            <FormGroup label="Scope" style={{ width: '100%' }}>
                              <Select
                                field={`${field}.scope`}
                                options={openScopes}
                                style={{ width: 200 }}
                              />
                            </FormGroup>
                          </FlexContainer>

                          <div className="terms-link-actions">
                            <Button
                              aria-label={messages.commonDelete}
                              category="light"
                              data-tooltip={messages.commonDelete}
                              icon="trash-alt"
                              onClick={remove}
                              size="small"
                              style={{ marginLeft: 10 }}
                            />
                          </div>
                        </FlexContainer>
                      );
                    }}
                  </ArrayField.Items>

                  <Button
                    category="light"
                    disabled={!nextScope}
                    icon="plus"
                    onClick={() =>
                      addWithInitialValue({
                        margins: { top: 40, bottom: 40, left: 20, right: 20 },
                        scope: nextScope
                      })
                    }
                    size="small"
                    style={{ marginTop: 5 }}
                    text="Add content"
                  />
                </>
              );
            }}
          </ArrayField>
        </FormSection>
      )}
    </SiteSettingsForm>
  );
};

export default SiteSettingsPdfs;
